import { Component, DestroyRef, OnInit, computed, inject, input, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule } from "@angular/forms";
import { TranslatePipe } from "@ngx-translate/core";
import { KENDO_COMBOBOX } from "@progress/kendo-angular-dropdowns";
import { Observable, map } from "rxjs";
import { EditOption } from "../../../../models/edit-option.model";
import { FlightEditLookups } from "../../../form/flight-edit-lookups";
import { FlightGroup } from "../../../form/flight-form-group";
import { sameName } from "../../../form/names";
import { ChangedFieldDirective } from "../../../changed-field.directive";

@Component({
    selector: "app-pilots-group",
    imports: [ChangedFieldDirective, ReactiveFormsModule, TranslatePipe, KENDO_COMBOBOX],
    templateUrl: "./pilots-group.component.html",
    styleUrl: "../../../edit-fields.scss",
})
export class PilotsGroupComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);

    readonly group = input.required<FlightGroup>();
    readonly lookups = input.required<FlightEditLookups>();

    private readonly pilotId = signal<number | null>(null);
    private readonly coPilotId = signal<number | null>(null);

    /** A pilot is either the pilot or the copilot of a flight, never both: each list leaves out the other's choice. */
    readonly pilots = computed(() => this.lookups().pilotChoices.filter((pilot) => pilot.id === null || pilot.id !== this.coPilotId()));
    readonly coPilots = computed(() => this.lookups().coPilotChoices.filter((pilot) => pilot.id !== this.pilotId()));
    /** As in the app: the copilot is never the pilot in command, unless the flight already says so. */
    readonly coPilotRoles = computed(() => {
        const current = this.group().controls.coPilotRoleId.value;
        return this.lookups().pilotRoles.filter((role) => role.abbreviation?.trim().toUpperCase() !== "PIL" || role.id === current);
    });

    /**
     * A copilot typed by hand, as the app saves one: trimmed and in upper case, without id. A name typed in full that
     * matches a pilot of the organization is that pilot.
     */
    readonly typedCoPilot = (text$: Observable<string>): Observable<EditOption> =>
        text$.pipe(map((name) => this.lookups().pilots.find((pilot) => sameName(pilot.name, name)) ?? { id: null, name: name.trim().toUpperCase() }));

    ngOnInit(): void {
        const { pilotId, coPilot } = this.group().controls;
        this.pilotId.set(pilotId.value);
        this.coPilotId.set(coPilot.value?.id ?? null);
        pilotId.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => this.pilotId.set(value));
        coPilot.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => this.coPilotId.set(value?.id ?? null));
    }
}
